import { DataSeriesEntry, TooltipFormatterParams, TooltipPosition } from "./echarts-options";
import { TooltipType } from "./voy-timeseries-chart-tooltip";

export interface WaypointData {
    name: string;
    value: string;
    xAxis: number;
    yAxis: number;
    isPilotPoint?: boolean;
    eta?: string;
}

export interface TimeseriesValues {
    // All arrays have to be the same length
    // Either both lower and upper are both present, or none of them are
    timestamps: number[];
    values: (number|null)[];
    lower?: (number|null)[];
    upper?: (number|null)[];
    direction?: (number|null|undefined)[];
    waypoints?: WaypointDataPoint[];
}

export interface TimeseriesData {
    name: string;
    fillArea?: boolean;
    color?: string;
    animate?: boolean;
    timeseries: TimeseriesValues;
    markArea?: { [index: string]: any };
    lineDash?: boolean;
}

export interface TimestampData {
    timestamp: number,
    isWp: boolean,
    routeIdx?: number,
    wpNumber?: number,
    eta?: string
}

export interface WaypointTimestamps {
    wpNumber: number
    timestamp: number
    eta?: string
}

// WP with added y-axis value
export interface WaypointDataPoint extends WaypointTimestamps {
    value: number
    eta?: string
}

export interface PilotPointTimestamps {
    departure: number
    arrival: number
}

export enum WaypointAndSeriesMatching {
    MatchWaypointsToSeries = "matchWaypointsToSeries", // each set of waypoints should be added to corresponding route series
    SameWaypointsForAllSeries = "sameWaypointsForAllSeries" // all series should share the same set of waypoints
}

export interface WeatherSeries {
    wind50M: TimeseriesData,
    currents: TimeseriesData,
    wave: TimeseriesData,
    swell: TimeseriesData
}

export enum RouteSeriesNames {
    Route1 = "Route 1",
    Route2 = "Route 2",
    Route3 = "Route 3",
    Route4 = "Route 4",
    Route5 = "Route 5",
}

export enum TimestampFillerSeriesName {
    TimestampFiller = "timestampFiller"
};

export enum UncertantyBandSeriesNames {
    Route1Upper = "route 1_upper",
    Route1Lower = "route 1_lower",
    Route2Upper = "route 2_upper",
    Route2Lower = "route 2_lower",
    Route3Upper = "route 3_upper",
    Route3Lower = "route 3_lower",
    Route4Upper = "route 4_upper",
    Route4Lower = "route 4_lower",
    Route5Upper = "route 5_upper",
    Route5Lower = "route 5_lower",
}

export type ChartSeriesNames = RouteSeriesNames | TimestampFillerSeriesName | UncertantyBandSeriesNames;

export enum ChartNames {
    Sog = "sog",
    Power = "power",
    Rpm = "rpm",
    Wind50M = "wind50M",
    Currents = "currents",
    Wave = "wave",
    Swell = "swell",
    Stw = "stw",
    CurrentImpact = "currentImpact",
    RollAngle = "rollAngle"
}

export const echartUtils = new class {
    /**
     * @name getAggregatedTimestamps
     * @description Need to aggregate the times for all the data series, 
     * since echarts tooltips don't really work unless the data is uniform,
     * i.e. they all need to have the same x-axis entries.
     */
    getAggregatedTimestamps(data: TimeseriesData[], routesAndWaypoints: WaypointTimestamps[][]) {
        // Using a map to avoid duplicating times
        let timesMap = new Map<number, TimestampData>();
        for (const dataSeries of data) {
            for (const timestamp of dataSeries.timeseries.timestamps) {
                timesMap.set(timestamp, { timestamp, isWp: false });
            }
        }

        // When adding 'markPoints' (the dots) to a graph, both timestamp and values must
        // be available for it. Therefore, waypoint timestamps must be added to the timesMap 
        // along with all the other data. There must be timestamps for WPs in order to 
        // find a corresponding value when displaying the visual dot in the graph.
        for (let routeIdx = 0; routeIdx < routesAndWaypoints.length; routeIdx++) {
            for (const wp of routesAndWaypoints[routeIdx]) {
                if (timesMap.has(wp.timestamp)) {
                    timesMap.get(wp.timestamp)!.isWp = true;
                    timesMap.get(wp.timestamp)!.wpNumber = wp.wpNumber;
                    timesMap.get(wp.timestamp)!.routeIdx = routeIdx;
                    timesMap.get(wp.timestamp)!.eta = wp.eta;
                    continue;
                }
                timesMap.set(wp.timestamp, { timestamp: wp.timestamp, isWp: true, routeIdx: routeIdx, wpNumber: wp.wpNumber, eta: wp.eta });
            }
        }

        // Sort timestamps
        const aggregatedTimes = Array.from(timesMap.values());
        return aggregatedTimes.sort((a, b) => a.timestamp - b.timestamp);
    }

    /**
     * @name createMainSeries
     * @description Main data series. This is creating one line in the chart
    */
    createMainSeries(options: {dataSeries: TimeseriesData, color: string|undefined, timeSeriesData: TimeseriesValues, timestamps: number[], showWaypoints: boolean, waypointData: WaypointData[]}): DataSeriesEntry {
        const {dataSeries, color, timeSeriesData, timestamps, showWaypoints, waypointData} = options;
        return {
            name: dataSeries.name,
            type: 'line',
            zlevel: 0,
            step: 'end',
            color: color,
            areaStyle: dataSeries.fillArea ? { opacity: 0.5 } : undefined,
            animation: dataSeries.animate ? true : false,
            showSymbol: false,
            hoverAnimation: false,
            data: timeSeriesData.values.map((v, i) => [timestamps[i], v]),
            markArea: dataSeries.markArea,
            markPoint: showWaypoints ? this.createWaypoints(waypointData) : undefined,
            lineStyle: {
                type: dataSeries.lineDash ? 'dashed' : 'solid'
            },
        }
    }

    /**
    * @name createWaypoints
    * @description Create waypoints with different styles for pilot points
    */
    createWaypoints(waypointData: WaypointData[]) {
        return {
            symbol: 'circle',
            // To show markPoints, any data here must also be present in the data property above,
            // ie. any markPoint timestamp and value must be present in the above data property
            data: waypointData.map(wp => {
                // For pilot points, the marker label also shows ETA and a "Pilot Point" note
                let labelText = wp.name;
                if (wp.isPilotPoint) {
                    if (wp.eta) {
                        const eta = new Date(wp.eta).toLocaleString('en-GB', {
                            year: 'numeric', month: '2-digit', day: '2-digit',
                            hour: '2-digit', minute: '2-digit', timeZone: 'UTC'
                        }).replace(',', '') + ' UTC';
                        labelText = `${wp.name}\nETA: ${eta}\nPilot Point`;
                    } else {
                        labelText = `${wp.name}\nPilot Point`;
                    }
                }

                return {
                    name: wp.name,
                    value: wp.value,
                    xAxis: wp.xAxis,
                    yAxis: wp.yAxis,
                    // Apply different styling for pilot points
                    symbolSize: 8,
                    itemStyle: {
                        color: wp.isPilotPoint ? "rgba(128, 0, 128, 1)" : undefined,
                        opacity: wp.isPilotPoint ? 1 : 0.5,
                        borderWidth: wp.isPilotPoint ? 2 : 1,
                        borderColor: wp.isPilotPoint ? "rgba(128, 0, 128, 1)" : "rgba(0, 0, 0, 1)",
                    },
                    emphasis: {
                        itemStyle: {
                            opacity: 1,
                            borderWidth: wp.isPilotPoint ? 2 : 1,
                            borderColor: wp.isPilotPoint ? "rgba(128, 0, 128, 1)" : "rgba(0, 0, 0, 1)",
                            shadowBlur: wp.isPilotPoint ? 3 : 2
                        },
                        label: {
                            show: true,
                            opacity: 1,
                            color: 'inherit',
                            fontWeight: wp.isPilotPoint ? 'bold' : 'normal',
                            formatter: labelText,
                            align: 'center'
                        },
                    },
                    // Store ETA and pilot point info for tooltip
                    wpEta: wp.eta,
                    wpIsPilot: wp.isPilotPoint
                };
            }),
            label: {
                offset: [0, -25],
                opacity: 0
            }
        }
    }

    /**
     * @name createLowerSeries
     * @description Main data series can have some uncertanty. To indicate
     * this uncertanty, this function can be used to create upper and lower series 
     * to create a visual uncertanty band on the main series. Must be used
     * in conjunction with createUpperSeries.
     */
    createLowerSeries(options: {dataSeries: TimeseriesData, timeSeriesData: TimeseriesValues, timestamps: number[]}): DataSeriesEntry {
        const {dataSeries, timeSeriesData, timestamps} = options;
        return {
            name: dataSeries.name + "_lower",
            type: 'line',
            zlevel: 0,
            step: 'end',
            animation: dataSeries.animate ? true : false,
            lineStyle: { opacity: 0 },
            symbol: 'none',
            hoverAnimation: false,
            data: timeSeriesData.lower!.map((v, i) => [timestamps[i], v]),
            stack: dataSeries.name + "_band",
        }
    }

    /**
     * @name createLowerSeries
     * @description Main data series can have some uncertanty. To indicate
     * this uncertanty, this function can be used to create upper and lower series 
     * to create a visual uncertanty band on the main series. Must be used
     * in conjunction with createLowerSeries
     */
    createUpperSeries(options: {dataSeries: TimeseriesData, timeSeriesData: TimeseriesValues, timestamps: number[], color: string|undefined}): DataSeriesEntry {
        const {dataSeries, timeSeriesData, timestamps, color} = options;
        return {
            name: dataSeries.name + "_upper",
            type: 'line',
            zlevel: 0,
            step: 'end',
            color,
            lineStyle: { opacity: 0 },
            areaStyle: { opacity: 0.2 },
            animation: dataSeries.animate ? true : false,
            symbol: 'none',
            hoverAnimation: false,
            data: timeSeriesData.upper!.map((v, i) => [timestamps[i], v]),
            stack: dataSeries.name + "_band"
        }
    }

    //* if the fillerSeries is causing performance issues, then they can be removed
    //* and timestamps should be added through the prepAndPadTimeseries function
    /**
     * @name createTimestampFillerSeries
     * @description Tooltip and axisPointer will not work correctly across multiple charts,
     * if all charts do not have the same xAxis entries (timestamps). A series might not
     * have same size and thus does not have same timestamps.
     * The easiest way is just to add this invisible series that contain all timestamps.
     * @param {TimeStampData[]} allTimeStamps
     * 
     * @returns {DataSeriesEntry} dataSeriesEntry
     */
    createTimestampFillerSeries(allTimestamps: TimestampData[]): DataSeriesEntry {
        return {
            name: TimestampFillerSeriesName.TimestampFiller,
            type: 'line',
            zlevel: 0,
            step: 'end',
            animation: false,
            showSymbol: false,
            symbol: 'none',
            hoverAnimation: false,
            data: allTimestamps.map((v, i) => [v.timestamp, 1]),
            lineStyle: {
                opacity: 0
            },
        }
    }

    createTooltip(ctx: any ) {
        return {
            shared: true,
            useHTML: true,
            trigger: 'axis',
            padding: 0,
            // tooltip will be fixed on the right if mouse hovering on the left, and on the left if hovering on the right. 
            //Docs: https://echarts.apache.org/en/option.html#tooltip.position 
            position: function (pos:number[], params:any, dom:HTMLElement, rect:any, size: {contentSize: number[], viewSize: number[]}) {
                const obj: TooltipPosition = {top: 50};
                obj[['left' , 'right'][+(pos[0] < size.viewSize[0] / 2)]] = 5;
                return obj;
            },
            borderWidth: 0,
            backgroundColor: 'transparent',
            formatter: this.tooltipFormatter(ctx)
        }
    }

    // example https://stackoverflow.com/questions/64442517/echarts-show-additional-info-in-tooltip
    tooltipFormatter(ctx: any) {
        return function (params: TooltipFormatterParams[]) {
            const timeSeriesData = ctx._timeSeriesData;
    
            for (let i = 0; i < params.length; i++) {
                const param = params[i];

                // timestampFiller should be ignored
                if (param.seriesName === TimestampFillerSeriesName.TimestampFiller) {
                    continue;
                }
                
                const timeseries = timeSeriesData.get(param.seriesName);

                if (param && timeseries?.direction && typeof (timeseries.direction[param.dataIndex]) === "number") {
                    param.directionValue = timeseries.direction[param.dataIndex]!
                }
            }

            // Detect if the hovered timestamp matches a waypoint, to show WP number, ETA and pilot point info
            let waypointInfo: { wpNumber: number, eta?: string, isPilotPoint: boolean } | null = null;
            const hoveredTimestamp = params[0]?.value?.[0];
            if (hoveredTimestamp != null && ctx.routesAndWaypoints) {
                for (let routeIdx = 0; routeIdx < ctx.routesAndWaypoints.length; routeIdx++) {
                    const wp = ctx.routesAndWaypoints[routeIdx].find((w: any) => w.timestamp === hoveredTimestamp);
                    if (wp) {
                        const pilotNumbers = ctx.pilotPointWaypointNumbers?.[routeIdx] || [];
                        waypointInfo = {
                            wpNumber: wp.wpNumber,
                            eta: wp.eta,
                            isPilotPoint: pilotNumbers.includes(wp.wpNumber)
                        };
                        break;
                    }
                }
            }
        
            ctx._tooltipElement.tooltipType = ctx.tooltipType || TooltipType.standart;
            ctx._tooltipElement.params = params;
            ctx._tooltipElement.waypointInfo = waypointInfo;
            return ctx._tooltipElement;
        }
    }

    createCurrentTimeMarker(currentTime: number, marklineColor: string): DataSeriesEntry {
        return {
            name: 'time',
            type: 'line',
            zlevel: 0,
            color: 'black',
            showSymbol: false,
            hoverAnimation: false,
            data: [],
            animation: false,
            markLine: currentTime <= 0 ? undefined : {
                symbol: 'none',
                data: [{
                    name: 'Current time',
                    xAxis: new Date(currentTime),
                    label: {
                        show: false,
                        "formatter": "Time"
                    },
                    lineStyle: {
                        "color": marklineColor,
                        "width": 3,
                        "type": "solid",
                        "smooth": true,
                    }
                }],
            }
        }
    }

    /**
     * @name normaliseTimeseries
     * @description Returns the series with strictly ascending, unique timestamps, which is
     * what prepAndPadTimeseries needs to align a series onto the shared time axis.
     * Duplicates keep the first sample, which is the one the alignment already plotted for
     * the duplicates it could handle. Sources that are already clean are returned as-is.
     */
    normaliseTimeseries(timeseries: TimeseriesValues): TimeseriesValues {
        let isClean = true;
        for (let i = 1; i < timeseries.timestamps.length; i++) {
            if (timeseries.timestamps[i] <= timeseries.timestamps[i - 1]) {
                isClean = false;
                break;
            }
        }

        if (isClean) {
            return timeseries;
        }

        const order = timeseries.timestamps.map((_, i) => i)
            .sort((a, b) => timeseries.timestamps[a] - timeseries.timestamps[b] || a - b);

        const keep: number[] = [];
        for (const i of order) {
            // Same timestamp as the entry already kept, so this sample is a duplicate
            if (keep.length && timeseries.timestamps[keep[keep.length - 1]] === timeseries.timestamps[i]) {
                continue;
            }
            keep.push(i);
        }

        const result: TimeseriesValues = {
            timestamps: keep.map(i => timeseries.timestamps[i]),
            values: keep.map(i => timeseries.values[i]),
            waypoints: timeseries.waypoints,
        };

        if (timeseries.lower && timeseries.upper) {
            result.lower = keep.map(i => timeseries.lower![i]);
            result.upper = keep.map(i => timeseries.upper![i]);
        }
        if (timeseries.direction) {
            result.direction = keep.map(i => timeseries.direction![i]);
        }

        return result;
    }

    /**
     * @name createLowerSeries
     * @description _prepAndPadTimeseries is normalising all datasets with eachother so
     * they have the same timestamps. Also filling in values on missing spots,
     * that would occur due to normalising timestamps.
     * 
     * Also creating a waypoints data if any waypoints have been provided.
     */
    prepAndPadTimeseries(times: TimestampData[], timeseries: TimeseriesValues, dataIdx: number, waypointAndSeriesMatching: string): TimeseriesValues {
        // The cursor walk below advances only on an exact match against 'times', which is
        // deduped and sorted. A repeated or out-of-order sample in the series therefore
        // desyncs the cursor for good, padding the rest of the chart with the last matched
        // value (a flat line). Normalise first so the walk always stays in step.
        timeseries = this.normaliseTimeseries(timeseries);

        const result: TimeseriesValues = {
            timestamps: [],
            values: [],
            waypoints: [],
        };

        if (timeseries.lower && timeseries.upper) {
            result.lower = [];
            result.upper = [];
        }

        if (timeseries.direction) {
            result.direction = [];
        }
        
        let currIdx = -1;
        for (const time of times)
        {
            if (timeseries.timestamps[currIdx] === timeseries.timestamps[currIdx+1]) {
                currIdx++; // duplicate timestamp, so skip it. NB: this will lead to missing timestamps in graph as it skips timestamps
            }
            if (timeseries.timestamps[currIdx+1] === time.timestamp)
            {
                currIdx++;
                result.timestamps.push(timeseries.timestamps[currIdx]);
                result.values.push(timeseries.values[currIdx]);
                if (timeseries.lower && timeseries.upper) {
                    result.lower!.push(timeseries.lower[currIdx]);
                    result.upper!.push(timeseries.upper[currIdx]! - timeseries.lower[currIdx]!);
                }
                if (timeseries.direction) {
                    result.direction!.push(timeseries.direction[currIdx]);
                }

                if (time.isWp
                    // add only WPs that match current series index, e.g. WPs that belong to the corresponding route
                    && ((waypointAndSeriesMatching === WaypointAndSeriesMatching.MatchWaypointsToSeries && dataIdx === time.routeIdx)
                        // add the same set of waypoints to all series, e.g. both Actual and Simulated values
                        || (waypointAndSeriesMatching === WaypointAndSeriesMatching.SameWaypointsForAllSeries))) {
                    result.waypoints!.push({
                        timestamp: timeseries.timestamps[currIdx],
                        value: timeseries.values[currIdx]!,
                        wpNumber: time.wpNumber!,
                        eta: time.eta
                    })
                }

                if (currIdx + 1 >= timeseries.timestamps.length) {
                    // End of data reached
                    break;
                }
            }
            else if (currIdx !== -1) {
                // checking if this timestamp is before the series data starts
                // if yes, use null instead of padding with last known value to avoid flat lines
                const isBeforeSeriesStart = time.timestamp < timeseries.timestamps[0];

                result.timestamps.push(time.timestamp);
                result.values.push(isBeforeSeriesStart ? null : timeseries.values[currIdx]);
                if (timeseries.lower && timeseries.upper) {
                    result.lower!.push(isBeforeSeriesStart ? null : timeseries.lower[currIdx]);
                    result.upper!.push(isBeforeSeriesStart ? null : (timeseries.upper[currIdx]! - timeseries.lower[currIdx]!));
                }
                if (timeseries.direction) {
                    result.direction!.push(isBeforeSeriesStart ? null : timeseries.direction[currIdx]);
                }

                if (!isBeforeSeriesStart && time.isWp && time.timestamp === timeseries.timestamps[currIdx]
                    // add only WPs that match current series index, e.g. WPs that belong to the corresponding route
                    && ((waypointAndSeriesMatching === WaypointAndSeriesMatching.MatchWaypointsToSeries && dataIdx === time.routeIdx)
                        // add the same set of waypoints to all series, e.g. both Actual and Simulated values
                        || (waypointAndSeriesMatching === WaypointAndSeriesMatching.SameWaypointsForAllSeries))) {
                    result.waypoints!.push({
                        timestamp: timeseries.timestamps[currIdx],
                        value: timeseries.values[currIdx]!,
                        wpNumber: time.wpNumber!,
                        eta: time.eta
                    })
                }
            }
            // insert null at start of graph
            else {
                result.timestamps.push(time.timestamp);
                result.values.push(null);
                if (timeseries.lower && timeseries.upper) {
                    result.lower!.push(null);
                    result.upper!.push(null);
                }
                if (timeseries.direction) {
                    result.direction!.push(null);
                }
            }
        }

        // filtering out NaN/null timestamps and sort by timestamp for chronological order
        const cleanedTimestamps: number[] = [];
        const cleanedValues: (number | null)[] = [];
        const cleanedLower: (number | null)[] | undefined = result.lower ? [] : undefined;
        const cleanedUpper: (number | null)[] | undefined = result.upper ? [] : undefined;
        const cleanedDirection: (number | null)[] | undefined = result.direction ? [] : undefined;

        const validDataPoints: Array<{
            timestamp: number,
            value: number | null,
            lower?: number | null,
            upper?: number | null,
            direction?: number | null
        }> = [];

        for (let i = 0; i < result.timestamps.length; i++) {
            if (result.timestamps[i] == null || isNaN(result.timestamps[i])) {
                continue;
            }

            validDataPoints.push({
                timestamp: result.timestamps[i],
                value: result.values[i],
                lower: result.lower?.[i],
                upper: result.upper?.[i],
                direction: result.direction?.[i]
            });
        }

        // sorting by timestamp
        validDataPoints.sort((a, b) => a.timestamp - b.timestamp);

        for (const point of validDataPoints) {
            cleanedTimestamps.push(point.timestamp);
            cleanedValues.push(point.value);
            if (cleanedLower && point.lower !== undefined) cleanedLower.push(point.lower);
            if (cleanedUpper && point.upper !== undefined) cleanedUpper.push(point.upper);
            if (cleanedDirection && point.direction !== undefined) cleanedDirection.push(point.direction);
        }

        return {
            timestamps: cleanedTimestamps,
            values: cleanedValues,
            lower: cleanedLower,
            upper: cleanedUpper,
            direction: cleanedDirection,
            waypoints: result.waypoints?.filter(wp => !isNaN(wp.timestamp))?.sort((a, b) => a.timestamp - b.timestamp)
        };
    }

    private _chartUnitMap = new Map([
        ["sog", "Kt"],
        ["power", "kW"],
        ["rpm", "RPM"],
        ["wind50M", "Kt"],
        ["currents", "Kt"],
        ["wave", "M"],
        ["swell", "M"],
        ["stw", "Kt"],
        ["currentImpact", "Kt"],
        ["rollAngle", "°"],
    ])

    getUnit(chartType: string) {
        if (this._chartUnitMap.has(chartType)) {
            return this._chartUnitMap.get(chartType)!;
        }
        console.error("Wrong input, Cannot determine graph units - input received:", chartType)
        return ""
    }
    
    private _chartNamesMap = new Map([
        ["sog", "Speed over Ground"],
        ["power", "Propulsion - ME Power"],
        ["rpm", "Propulsion - ME RPM"],
        ["wind50M", "Wind 50 M - Force"],
        ["currents", "Currents - Speed"],
        ["wave", "Wave - Height"],
        ["swell", "Swell - Height"],
        ["stw", "Speed Through Water"],
        ["currentImpact", "Current impact on speed"],
        ["rollAngle", "Predicted Roll Angle"],
    ])
    
    getChartTitle(chartType: string) {
        if (this._chartNamesMap.has(chartType)) {
            return this._chartNamesMap.get(chartType);
        }
        console.error("Wrong input, Cannot determine graph title - input received:", chartType)
        return ""
    }
}
